/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon,
  Card,
  DetailStack,
  Grid,
  ICON_BRIEFCASE_OUTLINE,
  ICON_EYE_OUTLINE,
  Popover,
  Stack,
  Text,
  UserPreview,
} from '@doveit/bricks';
import {
  JobApplicationSales,
  SerpJobApplication,
} from '../../../providers/api/dtos';
import { JobApplicationType } from '../../../domain/types';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import UpdateJobApplicationStatus from '../update-job-application-status/UpdateJobApplicationStatus';
import DateViewer from '../../../components/date-viewer/DateViewer';
import { useJobApplicationSerpData } from '../../hooks/use-job-application-serp-data/useJobApplicationSerpData';
import TextPreview from '../../../components/text-preview/TextPreview';
import { ThemeIcons } from '../../../theme/icons';
import ViewApplicantPopoverPreview from '../view-applicant-popover-preview/ViewApplicantPopoverPreview';
import * as styles from '../view-serp-job-application-card/ViewSerpJobApplicationCard.style';

export interface ViewJobApplicationCardProps extends React.AriaAttributes {
  serpJobApplication: SerpJobApplication,
  onJobApplicationUpdate?: VoidFunction,
}

const ViewJobApplicationCard: React.FC<ViewJobApplicationCardProps> = ({
  serpJobApplication,
  onJobApplicationUpdate,
}) => {
  const {
    jobApplication,
    applicant,
    jobPositionData,
    note,
    goToJobApplicationPage,
  } = useJobApplicationSerpData(serpJobApplication);

  const { isFranchising, provinces } = React.useMemo(() => {
    if (jobApplication.applicationType === JobApplicationType.SALES) {
      const salesJobApplication = jobApplication as JobApplicationSales;
      return {
        isFranchising: salesJobApplication.isFranchising,
        provinces: salesJobApplication.provinces,
      };
    }
    return {
      isFranchising: undefined,
      provinces: undefined,
    };
  }, [jobApplication]);

  return (
    <Card>
      <Card.Header
        primaryActions={[
          <ActionIcon
            aria-label="Visualizza candidatura"
            label="Visualizza"
            size="S"
            icon={{ path: ICON_EYE_OUTLINE }}
            onClick={goToJobApplicationPage}
          />,
        ]}
      >
        <ViewApplicantPopoverPreview
          applicantId={jobApplication.applicantId}
          isFranchising={isFranchising}
          trigger={(
            <UserPreview
              avatar={{ placeholder: ICON_BRIEFCASE_OUTLINE, icon: !isFranchising ? ThemeIcons.DOVE : undefined }}
              size="S"
              name={applicant?.name}
            >
              {applicant?.phoneNumber || applicant?.email}
            </UserPreview>
          )}
        />
      </Card.Header>
      <Card.Content>
        <Stack gap={150}>
          <div>
            <UpdateJobApplicationStatus
              jobApplication={jobApplication}
              size="XS"
              onSuccess={onJobApplicationUpdate}
            />
          </div>
          <Grid gutter={75}>
            <Grid.Unit size={1 / 2}>
              <DetailStack
                label="Posizione"
                aria-label="Posizione candidatura"
              >
                {jobPositionData.data?.name ?? NO_VALUE_SYMBOL}
              </DetailStack>
            </Grid.Unit>
            <Grid.Unit size={1 / 2}>
              {(!provinces || provinces.length === 0) && (
                <DetailStack
                  label="Provincia"
                  aria-label="Provincia candidatura"
                >
                  {NO_VALUE_SYMBOL}
                </DetailStack>
              )}
              {provinces && provinces.length === 1 && (
                <DetailStack
                  label="Provincia"
                  aria-label="Provincia candidatura"
                >
                  {provinces[0]}
                </DetailStack>
              )}
              {provinces && provinces.length > 1 && (
                <Popover
                  aria-label="Visualizza tutte le province"
                  trigger={(
                    <DetailStack
                      label="Province"
                      aria-label="Province candidatura"
                    >
                      {`${provinces[0]},+${provinces.length - 1}`}
                    </DetailStack>
                  )}
                  hasArrow
                >
                  <div css={styles.provincesPopoverWrapper}>
                    <Stack gap={75}>
                      {provinces.map((province) => (
                        <Text.Caption key={province}>{province}</Text.Caption>
                      ))}
                    </Stack>
                  </div>
                </Popover>
              )}
            </Grid.Unit>
            <Grid.Unit size={1 / 2}>
              <DetailStack label="Aggiunta">
                <DateViewer
                  stringDate={jobApplication.createdAt!}
                  checkIfToday
                />
              </DetailStack>
            </Grid.Unit>
          </Grid>

          {!!note && (
            <Card.Divider />
          )}

          {note && (
            <TextPreview
              text={note.text}
              author={note.author}
              date={note.date}
            />
          )}
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default ViewJobApplicationCard;
